/**
 * As automações (fase 2, #932): o contrato `/v1/automacoes`.
 *
 * As automações moram no serviço de automações, que é a fonte da verdade
 * (ADR-0021); o CRM traduz. Nenhum nome do serviço aparece aqui. O documento das
 * rotas para o front: `docs/planos/2026-10-19-fase-2-automacoes.md`.
 *
 * A Cliqfy (`operacao_cliqfy`) monta automações normais e com SQL; a empresa,
 * só as internas. Automação com SQL é a que tem `consulta_sql` ou `para_cada`.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';

/** `arquivada` sai de rascunho ou pausada e volta para rascunho; não dispara e some da lista. */
export const SituacaoDaAutomacaoSchema = z.enum([
  'rascunho',
  'em_homologacao',
  'ativa',
  'pausada',
  'arquivada',
]);

export const EventoDoGatilhoSchema = z.enum([
  'conversa.criada',
  'conversa.atribuida',
  'conversa.mensagem_recebida',
  'conversa.situacao_mudou',
  'conversa.resposta_de_botao',
  'oportunidade.etapa_mudou',
  'oportunidade.ganha',
  'oportunidade.perdida',
]);

const HorarioDaAgendaSchema = z
  .object({
    /** 0 (domingo) a 6; `null` = todo dia. */
    dia_da_semana: z.number().int().min(0).max(6).nullable(),
    hora: z.number().int().min(0).max(23),
    minuto: z.number().int().min(0).max(59),
  })
  .strict();

const GatilhoPorEventoSchema = z
  .object({ tipo: z.literal('evento'), evento: EventoDoGatilhoSchema })
  .strict();

const GatilhoPorAgendaSchema = z
  .object({
    tipo: z.literal('agenda'),
    /** Fuso IANA (`America/Sao_Paulo`). */
    fuso: z.string().min(1).max(64),
    horarios: z.array(HorarioDaAgendaSchema).min(1).max(50),
  })
  .strict();

/** O gatilho que se cria: por evento ou por agenda. */
export const GatilhoSchema = z.discriminatedUnion('tipo', [
  GatilhoPorEventoSchema,
  GatilhoPorAgendaSchema,
]);

/** Na resposta, também o `interno` (o lado que dispara um prazo, criado por modelo). */
export const GatilhoDaRespostaSchema = z.discriminatedUnion('tipo', [
  GatilhoPorEventoSchema.extend({ evento: z.string() }),
  GatilhoPorAgendaSchema,
  z.object({ tipo: z.literal('interno') }).strict(),
]);

export const CondicaoSchema = z
  .object({ expressao: z.string().trim().min(1).max(2_000) })
  .strict()
  .nullable();

const Expressao = z.string().max(2_000);
const IdDoNo = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'letras, números, _ e -, até 64');
const PosicaoSchema = z.object({ x: z.number(), y: z.number() }).strict().optional();

const NoDeAcaoSchema = z
  .object({
    id: IdDoNo,
    tipo: z.literal('acao'),
    /** Uma das ações do catálogo (`GET /v1/automacoes/blocos`). */
    acao: z.string().min(1).max(64),
    /** Nome do parâmetro → expressão JSONata (valor fixo entre aspas simples). */
    parametros: z.record(z.string().max(64), Expressao),
    posicao: PosicaoSchema,
  })
  .strict();

const NoDeConsultaSqlSchema = z
  .object({
    id: IdDoNo,
    tipo: z.literal('consulta_sql'),
    operacao_id: IdSchema,
    /** Expressões JSONata, na ordem dos parâmetros da operação. */
    parametros: z.array(Expressao).max(40),
    /** Nome sob o qual as linhas ficam para os próximos blocos. */
    resultado_em: z.string().regex(/^[a-z_][a-z0-9_]{0,63}$/),
    posicao: PosicaoSchema,
  })
  .strict();

const NoDeCondicaoSchema = z
  .object({ id: IdDoNo, tipo: z.literal('condicao'), expressao: Expressao, posicao: PosicaoSchema })
  .strict();

const NoDeEscolhaSchema = z
  .object({ id: IdDoNo, tipo: z.literal('escolha'), expressao: Expressao, posicao: PosicaoSchema })
  .strict();

const NoDeEsperarTempoSchema = z
  .object({
    id: IdDoNo,
    tipo: z.literal('esperar_tempo'),
    milissegundos: z
      .number()
      .int()
      .min(0)
      .max(366 * 24 * 3_600_000),
    horario_comercial: z
      .object({
        fuso: z.string().min(1).max(64),
        janelas: z
          .array(
            z
              .object({
                dia_da_semana: z.number().int().min(0).max(6),
                inicio_minuto: z.number().int().min(0).max(1440),
                fim_minuto: z.number().int().min(1).max(1440),
              })
              .strict(),
          )
          .min(1)
          .max(50),
      })
      .strict()
      .optional(),
    posicao: PosicaoSchema,
  })
  .strict();

const NoDeEsperarEventoSchema = z
  .object({
    id: IdDoNo,
    tipo: z.literal('esperar_evento'),
    evento: z.string().min(1).max(64),
    /** JSONata: a chave que liga o evento esperado a esta instância (`conversa_id`). */
    correlacao: Expressao,
    limite_ms: z.number().int().positive().optional(),
    posicao: PosicaoSchema,
  })
  .strict();

const LigacaoSchema = z
  .object({
    de: IdDoNo,
    para: IdDoNo,
    /** `sim`/`nao` (condição), `caso:<valor>`/`padrao` (escolha), `tempo_esgotado` (espera). */
    quando: z
      .string()
      .regex(/^(sim|nao|padrao|tempo_esgotado|caso:.{1,100})$/)
      .optional(),
  })
  .strict();

const NosSimples = [
  NoDeAcaoSchema,
  NoDeConsultaSqlSchema,
  NoDeCondicaoSchema,
  NoDeEscolhaSchema,
  NoDeEsperarTempoSchema,
  NoDeEsperarEventoSchema,
] as const;

/** O corpo de um `para_cada`: os mesmos blocos, sem outro `para_cada` dentro. */
const GrafoInternoSchema = z
  .object({
    inicio: IdDoNo,
    nos: z
      .array(z.discriminatedUnion('tipo', [...NosSimples]))
      .min(1)
      .max(100),
    ligacoes: z.array(LigacaoSchema).max(300),
  })
  .strict();

const NoParaCadaSchema = z
  .object({
    id: IdDoNo,
    tipo: z.literal('para_cada'),
    operacao_id: IdSchema,
    parametros: z.array(Expressao).max(40),
    item_em: z.string().regex(/^[a-z_][a-z0-9_]{0,63}$/),
    corpo: GrafoInternoSchema,
    posicao: PosicaoSchema,
  })
  .strict();

export const NoDaAutomacaoSchema = z.discriminatedUnion('tipo', [...NosSimples, NoParaCadaSchema]);

export const GrafoSchema = z
  .object({
    inicio: IdDoNo,
    nos: z.array(NoDaAutomacaoSchema).min(1).max(200),
    ligacoes: z.array(LigacaoSchema).max(600),
  })
  .strict();

/**
 * Mais um começo da automação, além do `gatilho`: outro evento, com o "Só se"
 * dele, entrando num bloco do fluxo (#1183). Um cliente novo e um que volta
 * podem cair na mesma URA por pontos diferentes, sem duas cópias dela. Só por
 * evento: a automação por agenda tem um começo só.
 *
 * A automação dispara quando o `gatilho` ou algum destes casa com o evento e
 * passa na própria condição; os começos são conferidos na ordem (o principal
 * primeiro), e o primeiro que passa decide a entrada. Um evento dispara a
 * automação uma vez só, mesmo que dois começos casem.
 */
export const OutroComecoSchema = z
  .object({
    evento: EventoDoGatilhoSchema,
    condicao: CondicaoSchema.default(null),
    /** O bloco onde este começo entra; `null` é o primeiro bloco do fluxo (`grafo.inicio`). */
    entrada: IdDoNo.nullable().default(null),
  })
  .strict();

const OutroComecoDaRespostaSchema = z
  .object({
    evento: z.string(),
    condicao: z.object({ expressao: z.string() }).strict().nullable(),
    entrada: z.string().nullable(),
  })
  .strict();

/** O que se cria, edita e valida. */
export const ConfiguracaoDaAutomacaoSchema = z
  .object({
    gatilho: GatilhoSchema,
    condicao: CondicaoSchema.default(null),
    /** Os outros começos, além do `gatilho`; vazio na automação de um começo só. */
    outros_comecos: z.array(OutroComecoSchema).max(50).default([]),
    grafo: GrafoSchema,
  })
  .strict();

export type ConfiguracaoDaAutomacao = z.infer<typeof ConfiguracaoDaAutomacaoSchema>;

const NomeDaAutomacao = z.string().trim().min(1).max(120);
const DescricaoDaAutomacao = z.string().trim().max(500);

/** Criar no editor: a configuração e, opcionalmente, o nome e a descrição. */
export const CriarAutomacaoSchema = ConfiguracaoDaAutomacaoSchema.extend({
  nome: NomeDaAutomacao.optional(),
  descricao: DescricaoDaAutomacao.optional(),
}).strict();

export type CriarAutomacao = z.infer<typeof CriarAutomacaoSchema>;

/** Nome e descrição: não criam versão nem mexem no que serve. Ao menos um. */
export const IdentificarAutomacaoSchema = z
  .object({ nome: NomeDaAutomacao.optional(), descricao: DescricaoDaAutomacao.optional() })
  .strict()
  .refine((x) => x.nome !== undefined || x.descricao !== undefined, {
    message: 'informe nome e/ou descricao',
  });

export const SimularAutomacaoSchema = z
  .object({
    /**
     * O evento de exemplo, com os nomes que as expressões leem
     * (`conversation_id`, `opportunity_id`…; ver o `contexto` de cada evento em
     * `GET /v1/automacoes/blocos`).
     */
    contexto: z.record(z.string(), z.unknown()).default({}),
    /** A versão que serve (`ativa`) ou a alteração em homologação (`pendente`). */
    versao: z.enum(['ativa', 'pendente']).default('ativa'),
    /**
     * Por qual começo simular: `0` é o `gatilho`; `1` em diante, os
     * `outros_comecos` na ordem. Decide o "Só se" conferido e o bloco de entrada.
     */
    comeco: z.number().int().min(0).default(0),
  })
  .strict();

/** Editar: a configuração inteira e a versão que a tela leu. */
export const EditarAutomacaoSchema = ConfiguracaoDaAutomacaoSchema.extend({
  versao: z.number().int().min(1),
}).strict();

export type EditarAutomacao = z.infer<typeof EditarAutomacaoSchema>;

const CamposDaAutomacao = {
  id: IdSchema,
  /**
   * Num modelo, a principal comanda o grupo (ligar, pausar e arquivar valem para
   * as internas); a interna só se vê pelo id. A criada no editor é principal.
   */
  principal: z.boolean(),
  nome: z.string().nullable(),
  descricao: z.string().nullable(),
  /** `personalizada` para a criada no editor; a chave do modelo para as outras. */
  modelo: z.string(),
  situacao: SituacaoDaAutomacaoSchema,
  versao: z.number().int(),
  /** Executa SQL (no fluxo, nos passos de um modelo ou na pendente): só a Cliqfy edita. */
  usa_sql: z.boolean(),
  gatilho: GatilhoDaRespostaSchema,
  condicao: z.object({ expressao: z.string() }).strict().nullable(),
  /** Os outros começos, além do `gatilho` (ver `OutroComecoSchema`). */
  outros_comecos: z.array(OutroComecoDaRespostaSchema).default([]),
  /** `null` para as automações de modelo no formato linear: não abrem no editor. */
  grafo: GrafoSchema.nullable(),
  /**
   * A configuração do modelo pronto de que a automação veio, no vocabulário de
   * `POST /v1/automacoes/a-partir-de-modelo`, lida de volta dos passos que ela
   * roda. É o que a tela mostra e edita no lugar do mapa
   * (`PATCH /v1/automacoes/{id}/configuracao-do-modelo`). `null` na criada no
   * editor e nos modelos que ainda não se editam assim.
   */
  configuracao_do_modelo: z
    .object({
      /** A chave do modelo no catálogo (`scheduled-records-to-crm`…). */
      modelo: z.string(),
      configuracao: z.record(z.string(), z.unknown()),
    })
    .strict()
    .nullable()
    .default(null),
};

export const AutomacaoSchema = z
  .object({
    ...CamposDaAutomacao,
    /** A versão em homologação de uma automação ativa que foi editada. */
    pendente: z.object(CamposDaAutomacao).strict().nullable(),
  })
  .strict();

export type AutomacaoDaApi = z.infer<typeof AutomacaoSchema>;

export const AutomacoesSchema = z
  .object({
    itens: z.array(
      z
        .object({
          id: IdSchema,
          nome: z.string().nullable(),
          descricao: z.string().nullable(),
          modelo: z.string(),
          situacao: SituacaoDaAutomacaoSchema,
          versao: z.number().int(),
          usa_sql: z.boolean(),
          gatilho: GatilhoDaRespostaSchema,
          /** Os eventos dos outros começos, para o resumo da lista. */
          outros_comecos: z.array(z.object({ evento: z.string() }).strict()).default([]),
        })
        .strict(),
    ),
  })
  .strict();

export type SituacaoDaAutomacao = z.infer<typeof SituacaoDaAutomacaoSchema>;
export type AutomacoesDaApi = z.infer<typeof AutomacoesSchema>;
/** Uma linha da lista: o resumo, sem o grafo nem a pendente. */
export type ItemDaListaDeAutomacoes = AutomacoesDaApi['itens'][number];

export const RestaurarAutomacaoSchema = z.object({ versao: z.number().int().min(1) }).strict();

export const ValidacaoDaAutomacaoSchema = z.object({ ok: z.literal(true) }).strict();

export const HistoricoDaAutomacaoSchema = z
  .object({
    itens: z.array(
      z
        .object({
          versao: z.number().int(),
          /** `criada`, `editada`, `situacao_mudou` ou `restaurada`. */
          acao: z.string(),
          autor_id: z.string(),
          em: z.string(),
          resumo: z.string().nullable(),
          foto: z.object(CamposDaAutomacao).strict().nullable(),
        })
        .strict(),
    ),
  })
  .strict();

export type HistoricoDaAutomacao = z.infer<typeof HistoricoDaAutomacaoSchema>;
export type VersaoNoHistorico = HistoricoDaAutomacao['itens'][number];

// ── Catálogo ──────────────────────────────────────────────────────────────

const OpcaoParaEscolherSchema = z.object({ valor: z.string(), rotulo: z.string() }).strict();

const ParametroDoBlocoSchema = z
  .object({
    nome: z.string(),
    titulo: z.string(),
    tipo: z.string(),
    obrigatorio: z.boolean(),
    /**
     * Os valores para escolher, quando o parâmetro tem uma lista fechada (a
     * etapa, a pessoa da atribuição, o motivo de perda). Vazio quando quem
     * pergunta não pode ler a lista: a tela pede a expressão. Ausente quando o
     * parâmetro não tem lista.
     */
    opcoes: z.array(OpcaoParaEscolherSchema).optional(),
    /** O nome da escolha que manda `null` ("Ninguém: volta para a fila"), quando a ação aceita. */
    opcao_nula: z.string().optional(),
    /** No tipo `campos`: as chaves que a ação aceita no objeto, com o nome da tela. */
    subcampos: z.array(z.object({ chave: z.string(), titulo: z.string() }).strict()).optional(),
    /** No tipo `lista_de_textos`: como se chama cada item ("Etiqueta"). */
    item: z.string().optional(),
    /** No tipo `texto`: texto longo, num campo de várias linhas. */
    longo: z.boolean().optional(),
    /**
     * `false` quando o valor não pode ser fixo: dado do cliente (o contato, o nome
     * dele) só vem do evento ou da linha. A tela não oferece texto fixo para ele.
     */
    valor_fixo: z.boolean().optional(),
    /** O nome da escolha vazia do opcional ("Sem motivo"): sem ela, a ação vai sem o dado. */
    vazio: z.string().optional(),
    /** O aviso quando a lista da organização vem vazia: onde cadastrar. */
    se_vazia: z.string().optional(),
  })
  .strict();

/**
 * Um campo do evento que a condição compara, com os valores possíveis prontos:
 * a tela oferece a escolha e não pede que a pessoa escreva o valor. As opções
 * são do CRM — as fixas (a situação da conversa, quem escreveu) e as da
 * organização (etapas, caixas, pessoas, motivos de perda), já com o nome.
 */
export const CampoDoEventoSchema = z
  .object({
    /** O caminho que a expressão lê: `message.author_role`. */
    caminho: z.string(),
    titulo: z.string(),
    /** `sim_nao` compara com verdadeiro ou falso; `opcao`, com um dos valores. */
    tipo: z.enum(['opcao', 'sim_nao']),
    /**
     * Os valores possíveis. Vazio num campo `opcao` quando quem pergunta não pode
     * ler a lista de onde eles vêm (a equipe, para quem não administra): a tela
     * pede o valor escrito.
     */
    opcoes: z.array(OpcaoParaEscolherSchema),
  })
  .strict();

export type CampoDoEvento = z.infer<typeof CampoDoEventoSchema>;

export const BlocosDoEditorSchema = z
  .object({
    eventos: z.array(
      z
        .object({
          evento: z.string(),
          titulo: z.string(),
          /** O que o evento traz para as expressões (`evento.<campo>`). */
          contexto: z.array(z.string()),
          /** O CRM ainda não envia este evento. */
          em_breve: z.boolean(),
          /** Os campos que se comparam, com os valores para escolher. */
          campos: z.array(CampoDoEventoSchema),
          /**
           * O que as ações usam do evento, pelo nome ("A conversa do evento"): o
           * editor oferece a escolha, e o caminho fica para o serviço.
           */
          referencias: z.array(
            z
              .object({
                tipo: z.enum(['conversa', 'oportunidade', 'pessoa']),
                caminho: z.string(),
                titulo: z.string(),
              })
              .strict(),
          ),
        })
        .strict(),
    ),
    nos: z.array(
      z
        .object({
          tipo: z.string(),
          titulo: z.string(),
          descricao: z.string(),
          saidas: z.array(z.string()),
          campos: z.array(ParametroDoBlocoSchema),
          usa_sql: z.boolean(),
        })
        .strict(),
    ),
    acoes: z.array(
      z
        .object({
          acao: z.string(),
          titulo: z.string(),
          parametros: z.array(ParametroDoBlocoSchema),
        })
        .strict(),
    ),
  })
  .strict();

const CamposDoModelo = {
  chave: z.string(),
  nome: z.string(),
  descricao: z.string(),
  origem: z.string(),
  /** O modelo lê o banco do cliente: só a Cliqfy o monta; os outros, a empresa. */
  usa_sql: z.boolean(),
  /** `false`: o modelo ainda não funciona com os eventos do CRM (criar é 409). */
  disponivel: z.boolean(),
};

export const ModelosDeAutomacaoSchema = z
  .object({ itens: z.array(z.object(CamposDoModelo).strict()) })
  .strict();

export const ModeloDeAutomacaoSchema = z
  .object({ ...CamposDoModelo, esquema: z.unknown() })
  .strict();

export type BlocosDoEditor = z.infer<typeof BlocosDoEditorSchema>;
export type ModelosDeAutomacao = z.infer<typeof ModelosDeAutomacaoSchema>;

// ── Execuções, espera e painel ────────────────────────────────────────────

export const ResultadoDaExecucaoDaAutomacaoSchema = z.enum(['concluida', 'ignorada', 'falhou']);

export const ExecucaoDaAutomacaoSchema = z
  .object({
    id: z.string(),
    automacao_id: z.string(),
    versao: z.number().int(),
    resultado: ResultadoDaExecucaoDaAutomacaoSchema,
    motivo: z.string().nullable(),
    gatilho: z.enum(['evento', 'agenda', 'interno']),
    /**
     * Por qual começo a execução entrou: `0` é o `gatilho`, `1` em diante os
     * `outros_comecos`; e o evento dele. `null` fora do disparo por evento, e nas
     * execuções de antes do registro.
     */
    comeco: z.number().int().min(0).nullable().default(null),
    evento_do_comeco: z.string().nullable().default(null),
    duracao_ms: z.number(),
    em: z.string(),
    /**
     * Por onde passou: o bloco, o resultado e o motivo, que é sempre uma chave
     * curta (`acao_recusada`, ou `outro`): o texto livre do serviço não sai.
     */
    passos: z.array(
      z
        .object({
          no: z.string().nullable(),
          tipo: z.string(),
          resultado: ResultadoDaExecucaoDaAutomacaoSchema,
          motivo: z.string().nullable(),
          saida: z.string().nullable(),
        })
        .strict(),
    ),
  })
  .strict();

export const ExecucoesDaAutomacaoSchema = z
  .object({ itens: z.array(ExecucaoDaAutomacaoSchema), proximo_cursor: z.string().nullable() })
  .strict();

export const InstanciasEmEsperaSchema = z
  .object({
    itens: z.array(
      z
        .object({
          id: z.string(),
          automacao_id: z.string(),
          versao: z.number().int(),
          no: z.string(),
          esperando: z.enum(['tempo', 'evento']),
          evento: z.string().nullable(),
          desde: z.string(),
        })
        .strict(),
    ),
    proximo_cursor: z.string().nullable(),
  })
  .strict();

export const CancelamentoDeEsperaSchema = z
  .object({ id: z.string(), situacao: z.enum(['cancelada', 'nao_estava_esperando']) })
  .strict();

export const JanelaDoPainelSchema = z.enum(['1h', '6h', '24h', '7d', '30d']);

export const PainelDasAutomacoesSchema = z
  .object({
    janela: z.object({ de: z.string(), ate: z.string() }).strict(),
    execucoes: z
      .object({
        total: z.number().int(),
        concluidas: z.number().int(),
        ignoradas: z.number().int(),
        falhas: z.number().int(),
        taxa_de_falha: z.number(),
      })
      .strict(),
    duracao_ms: z.object({ p50: z.number(), p95: z.number(), maximo: z.number() }).strict(),
    por_gatilho: z.array(
      z.object({ gatilho: z.string(), total: z.number().int(), falhas: z.number().int() }).strict(),
    ),
    falhas_recentes: z.array(
      z
        .object({
          execucao_id: z.string(),
          automacao_id: z.string(),
          em: z.string(),
          motivo: z.string().nullable(),
        })
        .strict(),
    ),
  })
  .strict();

export type PainelDasAutomacoes = z.infer<typeof PainelDasAutomacoesSchema>;

const PassoSchema = z
  .object({
    no: z.string().nullable(),
    tipo: z.string(),
    resultado: ResultadoDaExecucaoDaAutomacaoSchema,
    motivo: z.string().nullable(),
    saida: z.string().nullable(),
  })
  .strict();

/** O que a automação faria, sem efeito real. Nada é enviado, agendado ou gravado. */
export const SimulacaoDaAutomacaoSchema = z
  .object({
    /** Como numa execução; `esperando` = pararia num bloco de espera. */
    resultado: z.enum(['concluida', 'ignorada', 'falhou', 'esperando']),
    motivo: z.string().nullable(),
    passos: z.array(PassoSchema),
    /**
     * As ações que seriam pedidas ao CRM e os parâmetros que cada uma levaria, só
     * pelo nome: os valores avaliados podem vir do banco do cliente e não saem.
     */
    acoes: z.array(z.object({ acao: z.string(), parametros: z.array(z.string()) }).strict()),
    /** Os prazos que seriam agendados. */
    agendamentos: z.array(z.object({ em: z.string() }).strict()),
    /** Quantas linhas cada operação SQL leu, por `operacao_id` (em modo de teste). */
    contagens: z.record(z.string(), z.number()),
    espera_em: z.string().nullable(),
  })
  .strict();

// ── A configuração dos modelos, no vocabulário do CRM ─────────────────────────
//
// O serviço monta os modelos com a configuração no formato dele; o CRM expõe a
// sua, em português e com as ações do catálogo do CRM, e o adapter traduz. O
// esquema que a tela recebe (`GET /v1/automacoes/modelos/{chave}`) sai daqui.

const HorarioDaAgendaDoModelo = z
  .object({
    dia_da_semana: z.number().int().min(0).max(6).nullable(),
    hora: z.number().int().min(0).max(23),
    minuto: z.number().int().min(0).max(59),
  })
  .strict();

const AgendaDoModelo = z
  .object({
    fuso: z.string().min(1).max(64),
    horarios: z.array(HorarioDaAgendaDoModelo).min(1).max(50),
  })
  .strict();

const HorarioComercialDoModelo = z
  .object({
    fuso: z.string().min(1).max(64),
    janelas: z
      .array(
        z
          .object({
            dia_da_semana: z.number().int().min(0).max(6),
            inicio_minuto: z.number().int().min(0).max(1440),
            fim_minuto: z.number().int().min(1).max(1440),
          })
          .strict(),
      )
      .min(1)
      .max(50),
  })
  .strict();

/** Uma ação do catálogo do CRM (`GET /v1/automacoes/blocos`), com os parâmetros em JSONata. */
const AcaoDoModelo = z
  .object({
    acao: z.string().min(1).max(64),
    parametros: z.record(z.string().max(64), z.string().max(2_000)),
  })
  .strict();

/** A configuração de cada modelo, pela chave do catálogo. */
export const CONFIGURACOES_DOS_MODELOS = {
  'opportunity-owner-from-conversation-taker': z.object({}).strict(),
  'redistribute-on-no-first-reply': z
    .object({
      /** O prazo da primeira resposta, em minutos de horário comercial. */
      minutos_sem_resposta: z.number().int().positive().max(10_080),
      horario_comercial: HorarioComercialDoModelo,
      /** Modelo aprovado do WhatsApp para avisar o cliente; opcional. */
      modelo_de_mensagem_ao_cliente: z.string().min(1).max(200).optional(),
    })
    .strict(),
  'channel-menu-with-inactivity-close': z
    .object({
      /** O canal em que o menu aparece. */
      canal_id: z.string().min(1).max(100),
      texto_do_menu: z.string().trim().min(1).max(1_024),
      opcoes: z
        .array(
          z
            .object({
              id: z.string().regex(/^[a-z0-9_-]{1,40}$/),
              rotulo: z.string().trim().min(1).max(24),
              /** O que acontece ao escolher: só ações do CRM, nunca SQL. */
              acoes: z.array(AcaoDoModelo).min(1).max(10),
            })
            .strict(),
        )
        .min(1)
        .max(10)
        // A escolha do cliente volta só com o rótulo (#1012): dois iguais não dizem qual foi.
        .refine((opcoes) => new Set(opcoes.map((o) => o.rotulo)).size === opcoes.length, {
          message: 'rótulos repetidos',
        }),
      texto_do_lembrete: z.string().trim().min(1).max(1_024),
      lembrar_apos_minutos: z.number().int().positive().max(10_080),
      encerrar_apos_minutos: z.number().int().positive().max(10_080),
      motivo_do_encerramento: z.string().trim().min(3).max(280),
    })
    .strict(),
  'appointment-reminder-and-confirmation': z
    .object({
      agenda: AgendaDoModelo,
      /** A operação SQL de leitura que traz os compromissos. */
      operacao_id: IdSchema,
      parametros_da_operacao: z.array(z.string().trim().min(1).max(2_000)).max(40).optional(),
      /** Modelo aprovado do WhatsApp do lembrete. */
      modelo_do_lembrete: z.string().min(1).max(200),
      /** JSONata: a conversa de cada compromisso (padrão `compromisso.conversation_id`). */
      conversa: z.string().trim().min(1).max(2_000).optional(),
    })
    .strict(),
  'scheduled-records-to-crm': z
    .object({
      agenda: AgendaDoModelo,
      /** A operação SQL de leitura cujas linhas viram ações no CRM. */
      operacao_id: IdSchema,
      parametros_da_operacao: z.array(z.string().trim().min(1).max(2_000)).max(40).optional(),
      /** O nome de cada linha nas expressões da ação. */
      linha_em: z
        .string()
        .regex(/^[a-z_][a-z0-9_]{0,63}$/)
        .optional(),
      acao: AcaoDoModelo,
    })
    .strict(),
} as const satisfies Record<string, z.ZodType>;

export type ChaveDeModelo = keyof typeof CONFIGURACOES_DOS_MODELOS;

/** O esquema da configuração de um modelo (JSON Schema), ou `null` se o CRM não o conhece. */
export function esquemaDaConfiguracaoDoModelo(chave: string): Record<string, unknown> | null {
  const schema = (CONFIGURACOES_DOS_MODELOS as Record<string, z.ZodType>)[chave];
  return schema
    ? (z.toJSONSchema(schema, { target: 'draft-2020-12' }) as Record<string, unknown>)
    : null;
}

export const CriarAPartirDeModeloSchema = z
  .object({
    modelo: z.string().regex(/^[a-z0-9_:-]{1,100}$/),
    /** A configuração do modelo, conferida pelo esquema dele (`/modelos/{chave}`). */
    configuracao: z.record(z.string(), z.unknown()).default({}),
    nome: z.string().trim().min(1).max(120).optional(),
    descricao: z.string().trim().max(500).optional(),
  })
  .strict();

export type CriarAPartirDeModelo = z.infer<typeof CriarAPartirDeModeloSchema>;

/**
 * Editar a configuração de uma automação de modelo pronto: a mesma configuração
 * do criar, conferida pelo esquema do modelo dela, e a versão que a tela leu.
 */
export const EditarAutomacaoDoModeloSchema = z
  .object({
    configuracao: z.record(z.string(), z.unknown()),
    versao: z.number().int().min(1),
  })
  .strict();

export type EditarAutomacaoDoModelo = z.infer<typeof EditarAutomacaoDoModeloSchema>;

/** As automações que o modelo montou. A `principal` é a que a tela mostra, liga e pausa. */
export const CriadasDoModeloSchema = z
  .object({ principal: AutomacaoSchema, internas: z.array(AutomacaoSchema) })
  .strict();
